import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, TrendingUp } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { cashflowApi } from '../services/cashflowApi';
import { companyApi } from '../services/companyApi';
import { Company } from '../types/company';
import { Deal, Valuation } from '../types/deal';
import { Cashflow } from '../types/cashflow';
import { KIND_LABEL, KIND_STYLE, Vehicle, shortWon, won } from '../types/vehicle';
import { Metrics, computeMetrics, mult, pct } from '../lib/metrics';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { PortfolioInsights } from '../components/PortfolioInsights';
import { CompliancePanel } from '../components/CompliancePanel';

/**
 * 대시보드 — 자금원별 성적과 GRK 매출.
 *
 * ⚠️ **고유자금과 위탁자금을 합쳐서 보여주지 않는다.** 조합 통장의 돈은 GRK 자산이
 *    아니다. 합치면 회사 자산이 부풀려 보인다.
 * ⚠️ 지표는 저장하지 않고 원장에서 매번 계산한다 (`lib/metrics.ts`).
 */
export const DashboardPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [values, setValues] = useState<Record<number, Valuation>>({});
  const [loading, setLoading] = useState(true);
  const [tourRun, setTourRun] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d, f, lv, c] = await Promise.all([
        vehicleApi.list(), dealApi.list(), cashflowApi.list(), dealApi.latestValues(), companyApi.list(),
      ]);
      setVehicles(v); setDeals(d); setFlows(f); setValues(lv); setCompanies(c);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** 자금원별 잔여가치 — 끝난 건은 빼고 건별 최신 평가액을 더한다 */
  const residualOf = useCallback((vehicleId: number) =>
    deals
      .filter((d) => d.vehicleId === vehicleId && d.stage !== '회수' && d.stage !== '상각')
      .reduce((s, d) => s + (values[d.id]?.ourValue ?? 0), 0),
  [deals, values]);

  /** 평가액 없는 보유 건 — 있으면 TVPI · IRR 을 「—」로(0 으로 세면 전액 손실처럼 보인다) */
  const unvaluedIn = useCallback((ids: Set<number>) =>
    deals.some((d) => ids.has(d.vehicleId) && d.stage !== '회수' && d.stage !== '상각' && d.stage !== '검토' && !values[d.id]),
  [deals, values]);
  const blank = (m: Metrics, ids: Set<number>): Metrics => (unvaluedIn(ids) ? { ...m, tvpi: null, irr: null } : m);

  const rows = useMemo(() => vehicles.map((v) => ({
    v,
    m: blank(computeMetrics(flows.filter((f) => f.vehicleId === v.id), residualOf(v.id)), new Set([v.id])),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [vehicles, flows, residualOf, unvaluedIn]);

  const own = rows.filter((r) => r.v.kind === 'own');
  const ent = rows.filter((r) => r.v.kind === 'entrusted');

  /** GRK 매출 — 보수 합계. 조합 성과와 전혀 다른 숫자다 */
  const grkFee = flows.filter((f) => f.kind === 'fee').reduce((s, f) => s + f.amount, 0);
  const unchecked = vehicles.filter((v) => v.kind === 'entrusted' && !v.legalForm);

  const total = (list: typeof rows): Metrics => {
    const ids = new Set(list.map((r) => r.v.id));
    const res = list.reduce((s, r) => s + r.m.residual, 0);
    return blank(computeMetrics(flows.filter((f) => ids.has(f.vehicleId)), res), ids);
  };

  const TOUR: TourStep[] = useMemo(() => [
    {
      name: 'fee',
      target: '[data-tour="fee"]',
      title: 'GRK 매출',
      content: '원장에 적힌 관리보수·성과보수의 합계입니다. 맡은 돈이 손실이어도 보수는 나오므로 조합 성과와 따로 봅니다.',
    },
    {
      name: 'unchecked',
      target: '[data-tour="unchecked"]',
      title: '확인이 필요한 자금원',
      content: '위탁자금인데 조합인지 자문인지 아직 모르는 통장입니다. 사업자등록증 · 통장 명의 · 규약(또는 자문계약서)을 보면 정해집니다.',
    },
    {
      name: 'filter',
      target: '[data-tour="filter"]',
      title: '조회 범위 · 투자 시기',
      content: '위탁자금 · 고유자금 · 자금원 하나 중에서 고릅니다. 둘을 합친 「전체」는 두지 않습니다 — 합치면 회사 자산이 부풀려 보입니다.',
    },
    {
      name: 'kpi',
      target: '[data-tour="kpi"]',
      title: '핵심 성과 요약',
      content: '고른 범위의 총 투자원금 · 현재 가치 · 회수 · MOIC · IRR · 포트폴리오 수입니다.',
    },
    {
      name: 'risk',
      target: '[data-tour="risk"]',
      title: '리스크 등급',
      content: '보유 중인 건을 투자원금 기준으로 나눴습니다. 등급은 투자처 상세 › 리스크평가에서 매깁니다. 아래로 투자 배분 · 섹터별 성과 · 딜 프로세스 · Exit · 기여도가 이어집니다.',
    },
    {
      name: 'own',
      target: '[data-tour="own"]',
      title: '고유자금',
      content: 'GRK 가 직접 투자한 자금원입니다. 손익이 모두 회사에 귀속됩니다.',
    },
    {
      name: 'entrusted',
      target: '[data-tour="entrusted"]',
      title: '위탁자금',
      content: '기업에게 맡아 굴리는 돈입니다. GRK 자산이 아니라서 고유자금과 합계를 합치지 않습니다.',
    },
    {
      name: 'terms',
      target: '[data-tour="own"] thead',
      title: '지표 읽는 법',
      content: 'DPI는 실제로 돌려받은 배수, TVPI는 평가액까지 넣은 잠정 배수, IRR은 날짜를 반영한 연 수익률입니다. 모두 원장에서 매번 계산합니다.',
      placement: 'top',
    },
  ], []);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
          대시보드
          <TourButton onClick={() => setTourRun(true)} />
        </h1>
        <span className="text-xs text-slate-400">
          {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>

      {/* GRK 매출 — 따로 세운다. 조합 성과와 섞으면 둘 다 잘못 읽힌다 */}
      <section data-tour="fee" className="bg-white border border-slate-200 rounded-lg px-5 py-4 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <TrendingUp size={14} className="text-amber-600" /> GRK 매출 <span className="font-normal text-slate-400">보수 합계</span>
            </h2>
          </div>
          <div className="text-right flex-shrink-0">
            {grkFee > 0 ? (
              <>
                <div className="text-2xl font-bold text-amber-700 tabular-nums">{shortWon(grkFee)}</div>
                <div className="text-xs text-slate-400">{won(grkFee)}</div>
              </>
            ) : <div className="text-sm text-slate-400 pt-1">보수 기록 없음 — 원장 「보수」로 적습니다</div>}
          </div>
        </div>
      </section>

      {unchecked.length > 0 && (
        <div data-tour="unchecked" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
            <AlertTriangle size={14} /> 법적 형태 확인 전 {unchecked.length}건
          </h2>
          <p className="mt-1 text-xs text-amber-800">
            {unchecked.map((v) => v.name).join(' · ')} —{' '}
            <Link to="/vehicles" className="underline">자금원에서 확인</Link>
          </p>
        </div>
      )}

      <CompliancePanel />

      <PortfolioInsights vehicles={vehicles} deals={deals} companies={companies} flows={flows} values={values} />

      {([['own', own], ['entrusted', ent]] as const).map(([kind, list]) => {
        if (list.length === 0) return null;
        const t = total(list);
        return (
          <section key={kind} data-tour={kind} className="mb-5">
            <div className="flex items-baseline gap-2 mb-2">
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${KIND_STYLE[kind]}`}>
                {KIND_LABEL[kind]}
              </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs text-slate-400">
                    <th className="text-left font-medium px-4 py-2.5">자금원</th>
                    <th className="text-right font-medium px-2 py-2.5">투자</th>
                    <th className="text-right font-medium px-2 py-2.5">회수</th>
                    <th className="text-right font-medium px-2 py-2.5">평가</th>
                    <th className="text-right font-medium px-2 py-2.5">DPI</th>
                    <th className="text-right font-medium px-2 py-2.5">TVPI</th>
                    <th className="text-right font-medium px-2 py-2.5">IRR</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(({ v, m }) => (
                    <tr key={v.id} className="border-b border-slate-100">
                      <td className="px-4 py-3">
                        <Link to={`/vehicles/${v.id}`} className="font-medium text-slate-900 hover:text-indigo-600">{v.name}</Link>
                        {v.legalForm && <div className="text-xs text-slate-400">{v.legalForm}</div>}
                      </td>
                      <td className="px-2 py-3 text-right tabular-nums text-slate-900 font-medium whitespace-nowrap">{shortWon(m.invested)}</td>
                      <td className="px-2 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">{m.returned > 0 ? shortWon(m.returned) : '—'}</td>
                      <td className="px-2 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">{m.residual > 0 ? shortWon(m.residual) : '—'}</td>
                      <td className="px-2 py-3 text-right tabular-nums text-slate-700">{mult(m.dpi)}</td>
                      <td className={`px-2 py-3 text-right tabular-nums font-semibold ${
                        m.tvpi === null ? 'text-slate-400' : m.tvpi >= 1 ? 'text-emerald-700' : 'text-red-600'
                      }`}>{mult(m.tvpi)}</td>
                      <td className={`px-2 py-3 text-right tabular-nums font-semibold ${
                        m.irr === null ? 'text-slate-400' : m.irr >= 0 ? 'text-emerald-700' : 'text-red-600'
                      }`}>{pct(m.irr)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold">
                    <td className="px-4 py-2.5 text-xs text-slate-500">합계</td>
                    <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{shortWon(t.invested)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{t.returned > 0 ? shortWon(t.returned) : '—'}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">{t.residual > 0 ? shortWon(t.residual) : '—'}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{mult(t.dpi)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{mult(t.tvpi)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">{pct(t.irr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        );
      })}


      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
